import * as React from 'react'

import { Box, Typography, Snackbar, Alert, Divider } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { UserContext } from '../../UserContext'

import { sendToAnalytics } from '../../Analytics'

import ApiTokenManager from './ApiTokenManager'
import ApiAccessSourceHeader from './ApiAccessSourceHeader'

import { isKeywordGraphApp as getIsKeywordGraphApp } from '../../appVariant'

import { PanelRow, Panel, cardSx, columnSx } from '../panelLayout'
import McpServerConnect from '../McpServerCard'

export default function API(props) {
    const theme = useTheme()
    const [openSnackbar, setOpenSnackbar] = React.useState(false)
    const { id, username, email, plan, status } = React.useContext(UserContext)

    const [subscriptionPlan, setSubscriptionPlan] = React.useState('')

    const [subscriptionStatus, setSubscriptionStatus] = React.useState('')

    const [isRevealed, setIsRevealed] = React.useState(false)
    const [keyCreated, setKeyCreated] = React.useState(false)

    React.useEffect(() => {
        const isKeywordGraphApp = getIsKeywordGraphApp()

        const transformPlanName = plan => {
            if (isKeywordGraphApp) {
                switch (plan) {
                    case 'keywordgraph-premium-annual':
                    case 'keywordgraph-premium-annual-EUR-Yearly':
                        return 'KeywordGraph Premium Annual'
                    case 'keywordgraph-premium':
                    case 'keywordgraph-premium-EUR-Monthly':
                        return 'KeywordGraph Premium Monthly'
                    case 'keywordgraph-advanced-annual':
                    case 'keywordgraph-advanced-annual-EUR-Yearly':
                        return 'KeywordGraph Advanced Annual'
                    case 'keywordgraph-advanced':
                    case 'keywordgraph-advanced-EUR-Monthly':
                        return 'KeywordGraph Advanced Monthly'
                    case 'infranodus-cloud-annual':
                        return 'InfraNodus Cloud Annual'
                    case 'infranodus-cloud':
                        return 'InfraNodus Cloud Monthly'
                    case 'keywordgraph-basic-annual':
                    case 'keywordgraph-basic-annual-EUR-Yearly':
                        return 'KeywordGraph Basic Annual'
                    case 'keywordgraph-basic':
                    case 'keywordgraph-basic-EUR-Monthly':
                        return 'KeywordGraph Basic Monthly'
                    default:
                        return plan
                }
            }
            switch (plan) {
                case 'infranodus-enterprise-annual':
                    return 'InfraNodus Premium Annual'
                case 'infranodus-enterprise':
                case 'infranodus-enterprise-USD-Monthly':
                    return 'InfraNodus Premium Monthly'
                case 'infranodus-pro-annual':
                case 'infranodus-advanced-annual':
                    return 'InfraNodus Advanced Annual'
                case 'infranodus-pro':
                case 'infranodus-advanced':
                case 'infranodus-pro-USD-Monthly':
                    return 'InfraNodus Advanced Monthly'
                case 'infranodus-cloud-annual':
                    return 'InfraNodus Cloud Annual'
                case 'infranodus-cloud':
                case 'infranodus-cloud-USD-Monthly':
                    return 'InfraNodus Cloud Monthly'
                case 'infranodus-basic-annual':
                    return 'InfraNodus Basic Annual'
                case 'infranodus-basic':
                    return 'InfraNodus Basic Monthly'
                default:
                    return plan
            }
        }

        setSubscriptionPlan(transformPlanName(plan))
    }, [plan])

    React.useEffect(() => {
        const transformStatusName = status => {
            switch (status) {
                case 'invite':
                    return 'Invited'
                case 'active':
                    return 'Active'
                case 'cancelled':
                    return 'Cancelled'
                case 'Paused':
                    return 'Paused'
                default:
                    return status
            }
        }

        setSubscriptionStatus(transformStatusName(status))
    }, [status])

    const handleToggleReveal = () => {
        setIsRevealed(prev => !prev)
        sendToAnalytics({
            userId: username,
            event: 'view_api_key',
            data: { source: 'api_access_page' },
        })
    }

    return (
        <>
            <Box
                component="main"
                sx={{
                    backgroundColor: theme => theme.palette.background.default,
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    marginLeft: 4,
                }}
            >
                {/* Page intro + any account-status warning, above the split
                    so a warning stays prominent. */}
                <Box
                    sx={{
                        pt: 4,
                        pb: 1,
                        ml: { xs: 0, sm: 4 },
                        mr: { xs: 2, sm: 4 },
                        maxWidth: '800px',
                    }}
                >
                    <Typography
                        component="body1"
                        variant="body1"
                        align="left"
                        color="text.primary"
                    >
                        You can use the <a href="/api">InfraNodus API</a> to
                        implement knowledge graphs and GraphRAG workflows in
                        your LLM workflows, MCPs, and applications. InfraNodus
                        can extract network analysis insights from text data and
                        is particularly effective for topic modeling,
                        identifying content gaps, and enhancing AI workflows
                        with deeper contextual awareness through the knowledge
                        graph representation of textual information.
                    </Typography>

                    {(status == 'IN_TRIAL' ||
                        status == 'in_trial' ||
                        status == 'invite') && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                            <Typography
                                component="body1"
                                variant="body1"
                                align="left"
                                color="text.primary"
                            >
                                <strong>
                                    Note: While your account is In Trial status,
                                    you have a reduced number of AI and API
                                    queries available. If you'd like to get the
                                    full Cloud / Pro / Premium user quota, you
                                    can{' '}
                                    <a href="https://support.noduslabs.com/hc/en-us/requests/new">
                                        contact us
                                    </a>{' '}
                                    to authorize the immediate activation of
                                    your subscription.
                                </strong>
                            </Typography>
                        </Box>
                    )}
                    {(status == 'CANCELLED' || status == 'cancelled') && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                            <Typography
                                component="body1"
                                variant="body1"
                                align="left"
                                color="text.primary"
                            >
                                <strong>
                                    Warning: your account is Cancelled. If you'd
                                    like to get the full Cloud / Pro / Premium
                                    user AI and API quota, you can{' '}
                                    <a href="/subscription">
                                        reactivate your subscription
                                    </a>
                                    .
                                </strong>
                            </Typography>
                        </Box>
                    )}
                    {(status == 'PAUSED' || status == 'paused') && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                            <Typography
                                component="body1"
                                variant="body1"
                                align="left"
                                color="text.primary"
                            >
                                <strong>
                                    Warning: your account is Paused. If you'd
                                    like to get the full Cloud / Pro / Premium
                                    user API quota, you can{' '}
                                    <a href="/subscription">
                                        reactivate your subscription
                                    </a>{' '}
                                    or{' '}
                                    <a href="https://support.noduslabs.com/hc/en-us/requests/new">
                                        contact us
                                    </a>{' '}
                                    to authorize the immediate activation of
                                    your subscription.
                                </strong>
                            </Typography>
                        </Box>
                    )}
                </Box>

                <PanelRow
                    sx={{
                        mt: 3,
                        mb: 5,
                        ml: { xs: 0, sm: 4 },
                        mr: { xs: 2, sm: 4 },
                    }}
                >
                    {/* Your keys, and what governs their quota. */}
                    <Box
                        data-testid="api-keys-panel"
                        sx={{ ...cardSx(theme), ...columnSx }}
                    >
                        <ApiAccessSourceHeader
                            source={props.source}
                            keyCreated={keyCreated}
                        />
                        <ApiTokenManager
                            onKeyCreated={() => setKeyCreated(true)}
                        />
                        <Box sx={{ mt: 4 }}>
                            <Typography
                                component="body1"
                                variant="body1"
                                align="left"
                                color="text.primary"
                            >
                                To manage your subscription and increase the API
                                quota, please, go to the{' '}
                                <a href="/subscription">subscription page</a>.
                                You may need to log out and log back in again
                                after you change your subscription and create a
                                new API key for the quota to be set correctly.
                            </Typography>
                        </Box>
                    </Box>

                    {/* How to plug the key into an LLM client, what to read,
                        and where else the key works. The MCP setup opens
                        expanded — the key it asks for is right beside it. */}
                    <Box
                        data-testid="api-docs-column"
                        sx={{
                            ...columnSx,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <McpServerConnect defaultOpen onApiAccessPage />
                        <Panel title="InfraNodus API Documentation">
                            <Typography variant="body1">
                                •{' '}
                                <a href="/api/docs" target="_blank">
                                    API Documentation
                                </a>{' '}
                                — access points, data structure, and
                                authentication.
                                <br />•{' '}
                                <a
                                    href="https://support.noduslabs.com/hc/en-us/sections/18343587412252-AI-RAG-GraphRAG-and-LLM-Workflows"
                                    target="_blank"
                                >
                                    Workflow Examples
                                </a>{' '}
                                — building chatbots, AI agents, and GraphRAG
                                workflows.
                                <br />•{' '}
                                <a href="/mcp" target="_blank">
                                    MCP Server Documentation
                                </a>{' '}
                                — integrate InfraNodus into your favorite LLM
                                client.
                            </Typography>
                        </Panel>

                        <Panel title="Use your API key in test mode with other apps">
                            <Typography variant="body1">
                                •{' '}
                                <a href="/extension" target="_blank">
                                    InfraNodus browser extension for YouTube and
                                    web
                                </a>
                                <br />•{' '}
                                <a href="/obsidian-plugin" target="_blank">
                                    Obsidian graph view plugin
                                </a>
                                <br />•{' '}
                                <a href="/vscode-extension" target="_blank">
                                    VSCode graph view extension
                                </a>
                                <br />•{' '}
                                <a
                                    href="https://support.noduslabs.com/hc/en-us/articles/20234511169308-Using-InfraNodus-with-n8n-Automation-Tool"
                                    target="_blank"
                                >
                                    N8N automations
                                </a>{' '}
                                and{' '}
                                <a
                                    href="https://support.noduslabs.com/hc/en-us/articles/16192164141980-Using-InfraNodus-with-No-Code-AI-Automation-Tool-Make-Com"
                                    target="_blank"
                                >
                                    Make.Com automations
                                </a>
                                <br />• Other external tools via the{' '}
                                <a href="/api">InfraNodus GraphRAG API</a>
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 2 }}
                            >
                                The API is currently operating in test mode and
                                is subject to change. We can set up a dedicated
                                server for stable production use for Enterprise
                                clients. If you have any questions, please,
                                contact us via{' '}
                                <a href="https://support.noduslabs.com/hc/en-us/requests/new">
                                    support
                                </a>
                                .
                            </Typography>
                        </Panel>
                    </Box>
                </PanelRow>
            </Box>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    API key copied into your clipboard. Use Ctrl/Cmd + V to
                    paste it into your app.
                </Alert>
            </Snackbar>
        </>
    )
}

function convertToDate(dateStr) {
    try {
        const date = new Date(dateStr)

        if (isNaN(date.getTime())) {
            return 'N/A'
        }

        // Format the date to 'dd month yyyy, hh:mm'
        const day = date
            .getDate()
            .toString()
            .padStart(2, '0')
        const month = date.toLocaleString('default', { month: 'long' })
        const year = date.getFullYear()
        const hours = date
            .getHours()
            .toString()
            .padStart(2, '0')
        const minutes = date
            .getMinutes()
            .toString()
            .padStart(2, '0')

        return `${day} ${month} ${year}, ${hours}:${minutes}`
    } catch (error) {
        console.error('Error converting date:', error.message)
        return 'N/A'
    }
}
